import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, UserCheck, Eye, ExternalLink, Search, UserMinus } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function EmployeesPage() {
  const { user } = useAuth();
  const isHRAdmin = ["super_admin", "hr_admin"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees");
      setRows(data || []);
    } catch (e) {
      toast.error("Failed to load employees");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((e) => {
    if (!q.trim()) return true;
    const haystack = `${e.full_name || ""} ${e.work_email || ""} ${e.job_title || ""} ${e.department || ""}`.toLowerCase();
    return haystack.includes(q.toLowerCase());
  });

  return (
    <div className="fade-up">
      <div className="mb-6">
        <div className="label-eyebrow mb-2">People</div>
        <h1 className="font-display text-4xl font-bold flex items-center gap-3">
          <Users className="w-8 h-8" /> Employees
        </h1>
        <p className="text-[var(--cc-text-soft)] mt-1">Manage employee profiles, HR documents and policy acknowledgments. Generate a profile from any candidate with an accepted offer.</p>
      </div>

      <div className="cc-card p-4 mb-4 flex items-center gap-2">
        <Search className="w-4 h-4 opacity-50" />
        <input
          className="cc-input border-none shadow-none flex-1"
          placeholder="Search by name, email, job title…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="employee-search-input"
        />
        {isHRAdmin && (
          <Link to="/app/admin/leavers" className="btn-ghost text-xs inline-flex items-center gap-1.5 shrink-0" data-testid="employees-leavers-register-link">
            <UserMinus className="w-3.5 h-3.5" /> Leavers register
          </Link>
        )}
      </div>

      <div className="cc-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow">Name</th>
              <th className="px-5 py-3 label-eyebrow">Job Title</th>
              <th className="px-5 py-3 label-eyebrow">Department</th>
              <th className="px-5 py-3 label-eyebrow">Start Date</th>
              <th className="px-5 py-3 label-eyebrow">Status</th>
              <th className="px-5 py-3 label-eyebrow text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" className="text-center py-10 text-[var(--cc-text-soft)]">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="6" className="text-center py-10 text-[var(--cc-text-soft)]" data-testid="empty-employees">
                {rows.length === 0 ? "No employees yet — promote a candidate with an accepted offer from the Pipeline." : "No employees match your search."}
              </td></tr>
            )}
            {filtered.map((e) => (
              <tr key={e.id} className="border-t border-[var(--cc-border)] hover:bg-[var(--cc-surface)]" data-testid={`employee-row-${e.id}`}>
                <td className="px-5 py-3">
                  <div className="font-medium flex items-center gap-2">
                    <UserCheck className="w-4 h-4 opacity-50" />
                    {e.full_name || `${e.first_name} ${e.last_name}`}
                  </div>
                  <div className="text-[11px] text-[var(--cc-text-soft)] mt-0.5">{e.work_email}</div>
                </td>
                <td className="px-5 py-3">{e.job_title || "—"}</td>
                <td className="px-5 py-3 text-[var(--cc-text-soft)]">{e.department || "—"}</td>
                <td className="px-5 py-3 text-xs text-[var(--cc-text-soft)] whitespace-nowrap">{e.start_date || "—"}</td>
                <td className="px-5 py-3">
                  <span className={`pill ${e.employment_status === "leaver" ? "bg-red-100 text-red-800" : e.status === "active" ? "pill-success" : e.status === "on_leave" ? "pill-warning" : "pill-info"}`}>
                    {e.employment_status === "leaver" ? "leaver" : e.status?.replace("_", " ")}
                  </span>
                </td>
                <td className="px-5 py-3 text-right">
                  <Link to={`/app/employees/${e.id}`} className="btn-ghost p-1.5 inline-flex" title="View profile" data-testid={`view-employee-${e.id}`}>
                    <Eye className="w-4 h-4" />
                  </Link>
                  {isHRAdmin && (
                    <Link
                      to={`/app/employees/${e.id}?tab=leaver`}
                      className="btn-ghost p-1.5 inline-flex text-[var(--cc-accent)] ml-1"
                      title={e.employment_status === "leaver" ? "Leaver record" : "Mark as leaver"}
                      data-testid={`leaver-employee-${e.id}`}
                    >
                      <UserMinus className="w-4 h-4" />
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
